import { supabase } from "../../../components/SupabaseClient";
import { useProfile } from "../../../context/ProfileContext";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Button,
  IconButton,
  Box,
  Avatar,
  Chip,
  Stack,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import LockResetIcon from "@mui/icons-material/LockReset";
import MainPanelContent from "../components/MainPanelContent";
import { getProfileFieldError } from "../../../utils/userProfileValidation";
import { OCCUPATION_OPTIONS } from "../../../constants/userOptions";
import {
  ACCENT,
  ACCENT_DARK,
  ACCENT_TINT,
  SURFACE,
  SURFACE_MUTED,
  INK_SECONDARY,
  HAIRLINE,
  HOVER,
  SHADOW_SM,
} from "../../../theme/tokens";

// A titled, bordered card that groups a section of the profile.
const SectionCard = ({ title, children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      border: `1px solid ${HAIRLINE}`,
      borderRadius: 1.5, // RADIUS.md (12px) — matches the app card radius
      overflow: "hidden",
      bgcolor: SURFACE,
      boxShadow: SHADOW_SM,
      ...sx,
    }}
  >
    <Box
      sx={{
        px: 2.5,
        py: 1.25,
        bgcolor: SURFACE_MUTED,
        borderBottom: `1px solid ${HAIRLINE}`,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: INK_SECONDARY,
          fontSize: "0.75rem",
        }}
      >
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

// A "description list" row: muted label, value, zebra background + divider.
const Field = ({ label, children, last }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      alignItems: { sm: "center" },
      gap: { xs: 0.25, sm: 2 },
      px: 2.5,
      py: 1.5,
      minHeight: 52,
      bgcolor: SURFACE,
      borderBottom: last ? 0 : `1px solid ${HAIRLINE}`,
      transition: "background-color 0.15s",
      "&:hover": { bgcolor: HOVER },
      "&:hover .field-action": { opacity: 1 },
    }}
  >
    <Typography
      component="span"
      sx={{
        width: { sm: 190 },
        flexShrink: 0,
        color: "text.secondary",
        fontSize: "0.8125rem",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        width: "100%",
        fontSize: "0.9rem",
        color: "text.primary",
      }}
    >
      {children}
    </Box>
  </Box>
);

const Profile = ({ user }) => {
  const { profile, setProfile, profileLoading, profileError } = useProfile();

  const [editingFieldKey, setEditingFieldKey] = useState(null);
  const [currentEditValue, setCurrentEditValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editError, setEditError] = useState(null);

  const fieldError = getProfileFieldError(editingFieldKey, currentEditValue);
  const navigate = useNavigate();

  const handleEditClick = (fieldKey, currentValue) => {
    setEditingFieldKey(fieldKey);
    setCurrentEditValue(currentValue || "");
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setEditingFieldKey(null);
    setCurrentEditValue("");
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (editingFieldKey === null || !user || !user.id) return;
    if (fieldError) return;

    setIsSubmitting(true);
    setEditError(null);

    const profileFields = ["first_name", "last_name", "username", "email"];
    const affiliationFields = [
      "institution",
      "department",
      "advisor",
      "occupation",
    ];

    let rpcFunctionTarget;
    if (profileFields.includes(editingFieldKey)) {
      rpcFunctionTarget = "update_user_profile_field";
    } else if (affiliationFields.includes(editingFieldKey)) {
      rpcFunctionTarget = "update_user_affiliation_field";
    } else {
      setEditError(`Unknown field: ${editingFieldKey} for update.`);
      setIsSubmitting(false);
      return;
    }

    try {
      const { error: updateError } = await supabase.rpc(rpcFunctionTarget, {
        p_user_id: user.id,
        p_field_name: editingFieldKey,
        p_new_value: currentEditValue,
      });

      if (updateError) {
        setEditError(
          updateError.message || `Failed to update ${editingFieldKey}.`,
        );
      } else {
        setProfile((prevData) => {
          const newData = { ...prevData };
          if (profileFields.includes(editingFieldKey)) {
            newData.profile = {
              ...(newData.profile || {}),
              [editingFieldKey]: currentEditValue,
            };
          } else if (affiliationFields.includes(editingFieldKey)) {
            newData.affiliation = {
              ...(newData.affiliation || {}),
              [editingFieldKey]: currentEditValue,
            };
          }
          return newData;
        });
        handleCancelEdit();
      }
    } catch (err) {
      setEditError("An unexpected error occurred while updating the profile.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Read-only value (no edit affordance). wordBreak lets long, unbreakable
  // values (emails, UUIDs) wrap instead of overflowing on narrow screens.
  const renderData = (value) => (
    <Typography
      component="span"
      sx={{ fontSize: "0.9rem", wordBreak: "break-word" }}
    >
      {value || "N/A"}
    </Typography>
  );

  // Editable value: shows value with a hover-revealed pencil, or the inline editor.
  // Pass `options` to render a dropdown instead of a free-text field.
  const renderEditable = (fieldKey, value, options) => {
    if (editingFieldKey === fieldKey) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            size="small"
            select={Boolean(options)}
            value={currentEditValue}
            error={Boolean(fieldError)}
            helperText={fieldError}
            onChange={(e) => setCurrentEditValue(e.target.value)}
            variant="outlined"
            fullWidth
            autoFocus
            slotProps={{
              input: { sx: { fontSize: "0.85rem" } },
              htmlInput: { "aria-label": fieldKey },
            }}
            disabled={isSubmitting}
          >
            {options
              ? options.map((opt) => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: "0.85rem" }}>
                    {opt}
                  </MenuItem>
                ))
              : null}
          </TextField>
          <IconButton
            onClick={handleSaveEdit}
            aria-label="Save changes"
            disabled={isSubmitting || Boolean(fieldError)}
            color="primary"
            size="small"
          >
            <SaveIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="Cancel editing"
            onClick={handleCancelEdit}
            disabled={isSubmitting}
            size="small"
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: "0.9rem", minWidth: 0, wordBreak: "break-word" }}
        >
          {value || "N/A"}
        </Typography>
        <IconButton
          className="field-action"
          aria-label={`Edit ${fieldKey}`}
          onClick={() => handleEditClick(fieldKey, value)}
          size="small"
          sx={{
            flexShrink: 0,
            opacity: { xs: 1, sm: 0 },
            transition: "opacity 0.15s",
            color: ACCENT,
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  if (profileLoading) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading profile...</Typography>
      </Container>
    );
  }

  if (profileError) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Error: {profileError}</Alert>
      </Container>
    );
  }

  const safeProfile = profile?.profile || {};
  const safeGroups = profile?.groups || [];
  const safeAffiliation = profile?.affiliation || {};

  const firstName =
    safeProfile.first_name ||
    (user && user.email ? user.email.split("@")[0] : "User");
  const lastName = safeProfile.last_name || "";
  const fullName = `${firstName}${lastName ? ` ${lastName}` : ""}`;
  const initials =
    `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() || "U";

  const groupsTableSx = {
    border: `1px solid ${HAIRLINE}`,
    borderRadius: 1,
    overflow: "hidden",
    "& thead th": {
      color: INK_SECONDARY,
      bgcolor: SURFACE_MUTED,
      fontWeight: 700,
      fontSize: "0.7rem",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      borderColor: HAIRLINE,
      py: 1,
    },
    "& td, & tbody th": {
      borderColor: HAIRLINE,
      fontSize: "0.85rem",
      fontWeight: 400,
      bgcolor: "transparent",
      py: 1,
    },
    "& tbody tr:last-of-type td": { borderBottom: 0 },
    "& tbody tr": { transition: "background-color 0.15s" },
    "& tbody tr:hover": { bgcolor: HOVER },
  };

  return (
    <MainPanelContent title={`Welcome, ${fullName}!`}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "100%",
          minWidth: 900,
        }}
      >
        {editError && (
          <Alert severity="error" sx={{ borderRadius: 1.5 }}>
            {editError}
          </Alert>
        )}

        {/* Identity header */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            p: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: ACCENT,
                fontSize: "1.5rem",
                fontWeight: 700,
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
              >
                {safeAffiliation.occupation && (
                  <Chip
                    size="small"
                    label={safeAffiliation.occupation}
                    sx={{
                      bgcolor: ACCENT_TINT,
                      color: ACCENT_DARK,
                      fontWeight: 600,
                    }}
                  />
                )}
                {safeAffiliation.institution && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={safeAffiliation.institution}
                  />
                )}
              </Stack>
            </Box>
          </Box>

          <Button
            variant="outlinedButton"
            startIcon={<LockResetIcon />}
            onClick={() => navigate("/reset-password")}
          >
            Change Password
          </Button>
        </Paper>

        {/* EFabric User Profile (full width — holds the groups table) */}
        <SectionCard title="EFabric User Profile">
          <Field label="EFabric Username" index={0}>
            {renderData(safeProfile.efabric_username)}
          </Field>
          <Field label="EFabric User ID" index={1} last>
            {renderData(safeProfile.efabric_userid)}
          </Field>

          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "text.secondary",
                mb: 1.5,
              }}
            >
              Resource Group Assignments
            </Typography>
            {safeGroups && safeGroups.length > 0 ? (
              <Box sx={{ overflowX: "auto" }}>
                <Table
                  size="small"
                  aria-label="group access table"
                  sx={groupsTableSx}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Granted At</TableCell>
                      <TableCell>Expiration Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {safeGroups.map((g) => (
                      <TableRow key={g.id}>
                        <TableCell component="th" scope="row">
                          {g.name}
                        </TableCell>
                        <TableCell>{g.type}</TableCell>
                        <TableCell>
                          {g.granted_at
                            ? new Date(g.granted_at).toLocaleString()
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          {g.expires_at
                            ? new Date(g.expires_at).toLocaleString()
                            : "N/A"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography
                color="text.secondary"
                sx={{ fontSize: "0.875rem", fontStyle: "italic" }}
              >
                No groups assigned.
              </Typography>
            )}
          </Box>
        </SectionCard>

        {/* Keep both cards wide enough for labels, values, and inline editors. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 3,
            alignItems: "start",
          }}
        >
          <SectionCard title="Personal Profile">
            <Field label="Email" index={0}>
              {renderData(user.email)}
            </Field>
            <Field label="Joined Date" index={1}>
              {renderData(
                user.created_at
                  ? new Date(user.created_at).toLocaleDateString()
                  : "N/A",
              )}
            </Field>
            <Field label="First Name" index={2}>
              {renderEditable("first_name", safeProfile.first_name)}
            </Field>
            <Field label="Last Name" index={3}>
              {renderEditable("last_name", safeProfile.last_name)}
            </Field>
            <Field label="Personal Username" index={4} last>
              {renderEditable("username", safeProfile.username)}
            </Field>
          </SectionCard>

          <SectionCard title="User Affiliation">
            <Field label="Affiliation" index={0}>
              {renderEditable("institution", safeAffiliation.institution)}
            </Field>
            <Field label="Occupation" index={1}>
              {renderEditable(
                "occupation",
                safeAffiliation.occupation,
                OCCUPATION_OPTIONS,
              )}
            </Field>
            <Field label="Department" index={2}>
              {renderEditable("department", safeAffiliation.department)}
            </Field>
            <Field label="Advisor" index={3} last>
              {renderEditable("advisor", safeAffiliation.advisor)}
            </Field>
          </SectionCard>
        </Box>
      </Box>
    </MainPanelContent>
  );
};

export default Profile;
